// Pure helpers for the account page: names, dates and the pairing links the page reads. No DOM here, so the unit
// tests run them in Node.

export const KIND_NAMES: Record<string, string> = {
  browser: 'Browser',
  mac: 'Mac',
  ipad: 'iPad',
  iphone: 'iPhone',
  cli: 'Command line',
  agent: 'Agents on the web',
};

export const kindName = (kind: string) => KIND_NAMES[kind] ?? kind;

/** “Chrome on Mac”: what this browser calls itself in the Devices list. */
export function browserName(userAgent: string): string {
  const ua = userAgent;
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /(Chrome|Chromium|CriOS)\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Browser';
  const os = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /CrOS/.test(ua)
          ? 'ChromeOS'
          : /Mac OS X|Macintosh/.test(ua)
            ? 'Mac'
            : /Windows/.test(ua)
              ? 'Windows'
              : /Linux/.test(ua)
                ? 'Linux'
                : '';
  return os ? `${browser} on ${os}` : browser;
}

const RELATIVE = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' });
const DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const DATE_TIME = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** “just now”, “5 minutes ago”, “yesterday”, then a date. */
export function relativeTime(at: number, now = Date.now()): string {
  const s = Math.round((now - at) / 1000);
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return RELATIVE.format(-m, 'minute');
  const h = Math.round(m / 60);
  if (h < 24) return RELATIVE.format(-h, 'hour');
  const d = Math.round(h / 24);
  if (d < 7) return RELATIVE.format(-d, 'day');
  return DATE.format(at);
}

const CLOCK = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });
export const clockTime = (at: number) => CLOCK.format(at);
export const dateOnly = (at: number) => DATE.format(at);
export const dateTime = (at: number) => DATE_TIME.format(at);

export function words(n: number): string {
  return `${n.toLocaleString('en-GB')} ${n === 1 ? 'word' : 'words'}`;
}

/** Sentence case for the relay's terse messages (“sign in first” → “Sign in first.”). */
export function sentence(message: string): string {
  const m = message.trim();
  if (!m) return 'Something went wrong.';
  const first = m[0].toUpperCase() + m.slice(1);
  return /[.!?…:]$/.test(first) ? first : `${first}.`;
}

export interface PairingRef {
  pairingId: string;
  code: string;
}

/**
 * A pairing request from what the writer scanned or typed: the full link (`…/account#<id>.<code>`, or an older `…/pair#…`), its hash, or
 * `<id>.<code>`. The code may carry its dash; returns null for anything else.
 */
export function parsePairing(text: string): PairingRef | null {
  let s = text.trim();
  // The code alone, as another device shows it: the relay finds its request (`pairingId` left empty).
  const alone = s.toUpperCase().replace(/[-\s]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1');
  if (/^[0-9A-HJKMNP-TV-Z]{10}$/.test(alone)) return { pairingId: '', code: alone };
  const hash = s.indexOf('#');
  if (hash >= 0) s = s.slice(hash + 1);
  const dot = s.lastIndexOf('.');
  if (dot <= 0) return null;
  const pairingId = s.slice(0, dot);
  const code = s.slice(dot + 1).replace(/[-\s]/g, '').toUpperCase();
  if (!/^[A-Za-z0-9_-]{1,64}$/.test(pairingId) || !/^[0-9A-Z]{10}$/.test(code)) return null;
  return { pairingId, code };
}

/** A code as the requester shows it: `XXXXX-XXXXX`. */
export const displayCode = (code: string) => {
  const c = code.replace(/[-\s]/g, '').toUpperCase();
  return c.length === 10 ? `${c.slice(0, 5)}-${c.slice(5)}` : c;
};

/** A fingerprint in groups of four for reading aloud (`1a2b 3c4d …`). */
export const groupFingerprint = (fp: string) => fp.match(/.{1,4}/g)?.join(' ') ?? fp;

export interface Activity {
  at: number;
  device_id: string | null;
  action: string;
  detail: string | null;
}

/** One line of the audit list in words. `names` maps device ids and project ids to what the writer calls them. */
export function describeActivity(a: Activity, names: Map<string, string>): string {
  const text = describe(a, names);
  return text[0].toUpperCase() + text.slice(1);
}

function describe(a: Activity, names: Map<string, string>): string {
  let d: Record<string, unknown> = {};
  try {
    d = a.detail ? JSON.parse(a.detail) : {};
  } catch {
    // shown without detail
  }
  const device = (id: unknown) => (typeof id === 'string' && names.get(id)) || 'a device';
  const quoted = (s: unknown) => (typeof s === 'string' && s ? `“${s}”` : 'a device');
  const project = () => {
    const title = typeof d.project === 'string' ? names.get(d.project) : undefined;
    return title ? `“${title}”` : 'a project';
  };
  const person = (id: unknown) => {
    const name = typeof id === 'string' ? names.get(id) : undefined;
    return name ? `“${name}”` : 'a writer';
  };
  // A share row written to the owner's audit carries the member; the member's copy carries the owner.
  const ownerRow = typeof d.owner !== 'string';
  switch (a.action) {
    case 'account.created':
      return 'Account created';
    case 'signed.in':
      return `${quoted((d.device as { name?: string } | undefined)?.name)} signed in with a passkey`;
    case 'device.paired':
      return `${quoted(d.name)} added by pairing`;
    case 'device.revoked': {
      const who = device(a.device_id);
      const why = String(d.why ?? '');
      if (why === 'refresh token reused') return `${who} signed out: its session was used somewhere else`;
      if (why.startsWith('revoked by')) return `${who} signed out from ${device(why.slice('revoked by '.length))}`;
      return `${who} signed out`;
    }
    case 'recovery.used':
      return 'Recovery key used';
    case 'recovery.replaced':
      return 'Recovery key replaced';
    case 'passkey.added':
      return d.viaRecovery ? 'Passkey added with the recovery key' : 'Passkey added';
    case 'passkey.removed':
      return 'Passkey removed';
    case 'vault.copyAdded':
      return 'A passkey can now unlock the vault';
    case 'project.added':
      return `${project()} put on the web`;
    case 'project.keyChanged':
      return `${project()} given a new key`;
    case 'project.removed':
      return `${project()} taken off the web`;
    case 'agents.on':
      return `Agents on the web turned on for ${project()}`;
    case 'agents.off':
      return `Agents on the web turned off for ${project()}`;
    case 'agents.granted':
      return `${quoted(d.client)} connected as an agent`;
    case 'agents.revoked':
      return `${quoted(d.client)} disconnected as an agent`;
    // Shared projects (PROTOCOL.md §10.10). A row on the member's side names the `owner`; the owner's names the
    // `member`. Names come from `shareNames`; a writer the page can't name is "a writer".
    case 'share.created':
      return `An invitation to join ${project()} as a ${roleName(d.role)} made`;
    case 'share.revoked':
      return `An invitation to join ${project()} withdrawn`;
    case 'share.accepted':
      return ownerRow ? `${person(d.member)} accepted your invitation to join ${project()} as a ${roleName(d.role)}` : `You accepted an invitation from ${person(d.owner)} to join ${project()} as a ${roleName(d.role)}`;
    case 'member.confirmed':
      return ownerRow ? `You confirmed ${person(d.member)} as a ${roleName(d.role)} ${of(d.role)} ${project()}` : `${person(d.owner)} confirmed you as a ${roleName(d.role)} ${of(d.role)} ${project()}`;
    case 'member.roleChanged':
      return ownerRow ? `${person(d.member)} is now a ${roleName(d.role)} ${of(d.role)} ${project()}` : `${person(d.owner)} made you a ${roleName(d.role)} ${of(d.role)} ${project()}`;
    case 'member.removed':
      return ownerRow ? `You removed ${person(d.member)} from ${project()}` : `${person(d.owner)} removed you from ${project()}`;
    case 'member.left':
      return ownerRow ? `${person(d.member)} left ${project()}` : `You left ${project()}`;
    case 'project.rotated':
      return ownerRow ? `${project()} given a new key for its members` : `${person(d.owner)} gave ${project()} a new key`;
    case 'exchangeKey.set':
      return 'Key for sharing projects made';
    case 'exchangeKey.replaced':
      return 'Key for sharing projects replaced';
    default:
      return a.action;
  }
}

/** A share role as the writer reads it. */
export const roleName = (role: unknown) => (role === 'cowriter' ? 'co-writer' : role === 'commenter' ? 'commenter' : 'member');

/** "a co-writer of", "a commenter on". */
const of = (role: unknown) => (role === 'commenter' ? 'on' : 'of');

/**
 * Whose names the share rows of an audit list need, beyond devices and the writer's own projects: the members of each
 * of the writer's projects named in an owner's row (`listMembers` has them, removed ones for 30 days), and the owners
 * and titles of projects shared with the writer when any member's row is there (`listShares`, each opened with the AVK).
 */
export function shareNames(activity: Activity[]): { memberProjects: string[]; shared: boolean } {
  const memberProjects = new Set<string>();
  let shared = false;
  for (const a of activity) {
    if (!/^(share|member|project\.rotated)/.test(a.action)) continue;
    let d: Record<string, unknown> = {};
    try {
      d = a.detail ? JSON.parse(a.detail) : {};
    } catch {
      continue;
    }
    if (typeof d.owner === 'string') shared = true;
    else if (typeof d.member === 'string' && typeof d.project === 'string') memberProjects.add(d.project);
  }
  return { memberProjects: [...memberProjects], shared };
}

/** Device names the audit list can use: current devices, and the names recorded when others signed in or paired. */
export function deviceNames(devices: { id: string; name: string }[], activity: Activity[]): Map<string, string> {
  const names = new Map<string, string>();
  for (const a of activity) {
    try {
      const d = a.detail ? JSON.parse(a.detail) : {};
      if (a.action === 'device.paired' && typeof d.device === 'string' && typeof d.name === 'string') names.set(d.device, `“${d.name}”`);
      if ((a.action === 'signed.in' || a.action === 'account.created') && a.device_id && typeof d.device?.name === 'string') {
        names.set(a.device_id, `“${d.device.name}”`);
      }
    } catch {
      // skip
    }
  }
  for (const d of devices) names.set(d.id, `“${d.name}”`);
  return names;
}
